import type { FormArray, ValidationErrors, ValidatorFn } from '@angular/forms';

export class FormArrayValidators {
  /**
   * Checks whether the form array has at least as many
   * entries as defined by the threshold.
   */
  static min(threshold: number): ValidatorFn {
    return (array: FormArray): ValidationErrors | null => {
      if (!array) {
        return null;
      }

      if (!array.value || array.value.length < threshold) {
        return { min: { valid: false } };
      }

      return null;
    };
  }

  /**
   * Checks whether the form array has at most as many
   * entries as defined by the threshold.
   */
  static max(threshold: number): ValidatorFn {
    return (array: FormArray): ValidationErrors | null => {
      if (!array) {
        return null;
      }

      if (array.value && array.value?.length > threshold) {
        return { max: { valid: false } };
      }

      return null;
    };
  }

  /**
   * Checks whether the form array contains a given value.
   */
  static contains(value: unknown): ValidatorFn {
    return (array: FormArray): ValidationErrors | null => {
      if (!array) {
        return null;
      }

      if (!array.value?.includes(value)) {
        return { contains: { valid: false } };
      }

      return null;
    };
  }

  static containsMax(value: unknown, max: number): ValidatorFn {
    return (array: FormArray): ValidationErrors | null => {
      if (!array) {
        return null;
      }

      if (
        (array.value?.filter((v: unknown) => v === value).length ?? 0) > max
      ) {
        return { containsMax: { valid: false } };
      }

      return null;
    };
  }

  static containsMin(value: unknown, min: number): ValidatorFn {
    return (array: FormArray): ValidationErrors | null => {
      if (!array) {
        return null;
      }

      if (
        (array.value?.filter((v: unknown) => v === value).length ?? 0) < min
      ) {
        return { containsMin: { valid: false } };
      }

      return null;
    };
  }

  static containsExactly(value: unknown, count: number): ValidatorFn {
    return (array: FormArray): ValidationErrors | null => {
      if (!array) {
        return null;
      }

      if (
        (array.value?.filter((v: unknown) => v === value).length ?? 0) !== count
      ) {
        return { containsExactly: { valid: false } };
      }

      return null;
    };
  }
}
